import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { errorToast, successToast } from "@/app/_module/services/helper";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { getSupabaseClient } from "@/app/_module/services/supabaseClient";
import { removeRealtimeChannel } from "@/app/_module/services/realtimeTeardown";
import { useRealtimeRejoin } from "@/app/_module/services/realtimeHealth";
import aiCallingQueryKeys from "../services/keys";
import {
  getAgentConfig,
  createAgentConfig,
  updateAgentConfig,
  testAgentConnection,
  getVoices,
  getProviders,
  getPromptTemplates,
  generateScript,
  getCampaigns,
  getCampaign,
  createCampaign,
  updateCampaign,
  populateCampaignLeads,
  startCampaign,
  pauseCampaign,
  resumeCampaign,
  getCampaignLeads,
  getCampaignStats,
  getDashboard,
  getActiveCalls,
  getRecentDispositions,
  getCurrentUsage,
  getUsageHistory,
  instantCall,
  getTextingEligibility,
  getTextingStats,
  getTextConversations,
  getTextConversation,
  startAiText,
  takeOverTextConversation,
  closeTextConversation,
} from "../services/aiCallingService";
import { a2pService } from "@/app/_module/services/a2pService";
import type {
  CreateAiAgentConfigParams,
  UpdateAiAgentConfigParams,
  CreateCampaignParams,
  UpdateCampaignParams,
  PopulateLeadsParams,
  StartAiTextParams,
} from "../types/ai-calling.types";

/** HTTP 412 from POST /campaigns/:id/start — consent basis not attested yet. */
export const isPreconditionFailed = (err: any): boolean =>
  err?.code === 412 || err?.code === "412" || err?.response?.status === 412;

// ============================================================
// Agent Config Hooks
// ============================================================

export const useAgentConfig = () => {
  return useQuery({
    queryKey: aiCallingQueryKeys.config(),
    queryFn: getAgentConfig,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useCreateAgentConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAiAgentConfigParams) => createAgentConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.config() });
      successToast("AI agent configuration created");
    },
    onError: () => errorToast("Failed to create AI agent configuration"),
  });
};

export const useUpdateAgentConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAiAgentConfigParams }) =>
      updateAgentConfig(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.config() });
      successToast("AI agent configuration updated");
    },
    onError: () => errorToast("Failed to update AI agent configuration"),
  });
};

export const useTestAgentConnection = () => {
  return useMutation({
    mutationFn: ({ id, agentId }: { id: string; agentId: string }) =>
      testAgentConnection(id, agentId),
    onSuccess: (data) => {
      if (data.success) {
        successToast("Connection successful");
      } else {
        errorToast(data.message || "Connection test failed");
      }
    },
    onError: () => errorToast("Failed to test connection"),
  });
};

export const useVoices = (provider?: string) => {
  return useQuery({
    queryKey: aiCallingQueryKeys.voices(provider),
    queryFn: () => getVoices(provider),
    staleTime: 30 * 60 * 1000, // 30 min — voices rarely change
    gcTime: 60 * 60 * 1000,
  });
};

export const useVoiceProviders = () => {
  return useQuery({
    queryKey: aiCallingQueryKeys.providers(),
    queryFn: getProviders,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
};

export const usePromptTemplates = () => {
  return useQuery({
    queryKey: aiCallingQueryKeys.promptTemplates(),
    queryFn: getPromptTemplates,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
};

export const useGenerateScript = () => {
  return useMutation({
    mutationFn: (params: {
      campaignType: string;
      agencyName?: string;
      targetAudience?: string;
      tone?: string;
      customInstructions?: string;
    }) => generateScript(params),
    onSuccess: () => successToast("Script generated successfully"),
    onError: () => errorToast("Failed to generate script. Please try again."),
  });
};

// ============================================================
// Campaign Hooks
// ============================================================

export const useCampaigns = (page = 1, pageSize = 20) => {
  return useQuery({
    queryKey: aiCallingQueryKeys.campaignList({ page, pageSize }),
    queryFn: () => getCampaigns(page, pageSize),
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useCampaign = (id: string) => {
  return useQuery({
    queryKey: aiCallingQueryKeys.campaignDetail(id),
    queryFn: () => getCampaign(id),
    enabled: !!id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useCreateCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCampaignParams) => createCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.campaigns() });
      successToast("Campaign created successfully");
    },
    onError: () => errorToast("Failed to create campaign"),
  });
};

export const useUpdateCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCampaignParams }) =>
      updateCampaign(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.campaigns() });
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.campaignDetail(id) });
      successToast("Campaign updated");
    },
    onError: () => errorToast("Failed to update campaign"),
  });
};

export const usePopulateCampaignLeads = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PopulateLeadsParams }) =>
      populateCampaignLeads(id, data),
    onSuccess: (result, { id }) => {
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.campaignDetail(id) });
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.campaignLeads(id) });
      successToast(`Populated campaign with ${result.totalPopulated} leads`);
    },
    onError: () => errorToast("Failed to populate leads"),
  });
};

export const useStartCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => startCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.campaigns() });
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.campaignDetail(id) });
      successToast("Campaign started");
    },
    onError: (err: any) => {
      // 412 = consent basis missing; CampaignDetailView shows the message inline.
      if (isPreconditionFailed(err)) return;
      errorToast("Failed to start campaign");
    },
  });
};

export const usePauseCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pauseCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.campaigns() });
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.campaignDetail(id) });
      successToast("Campaign paused");
    },
    onError: () => errorToast("Failed to pause campaign"),
  });
};

export const useResumeCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resumeCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.campaigns() });
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.campaignDetail(id) });
      successToast("Campaign resumed");
    },
    onError: () => errorToast("Failed to resume campaign"),
  });
};

export const useCampaignLeads = (
  id: string,
  status?: string,
  page = 1,
  limit = 20,
) => {
  return useQuery({
    queryKey: aiCallingQueryKeys.campaignLeads(id, { status, page }),
    queryFn: () => getCampaignLeads(id, status, page, limit),
    enabled: !!id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useCampaignStats = (id: string) => {
  return useQuery({
    queryKey: aiCallingQueryKeys.campaignStats(id),
    queryFn: () => getCampaignStats(id),
    enabled: !!id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 60 * 1000, // Poll every 60s for stats
  });
};

// ============================================================
// Monitoring Hooks
// ============================================================

export const useAiDashboard = () => {
  return useQuery({
    queryKey: aiCallingQueryKeys.dashboard(),
    queryFn: getDashboard,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 60 * 1000, // Poll every 60s for dashboard data
    refetchOnWindowFocus: true,
  });
};

export const useActiveCalls = () => {
  return useQuery({
    queryKey: aiCallingQueryKeys.activeCalls(),
    queryFn: getActiveCalls,
    staleTime: 0,
    refetchInterval: 30 * 1000, // Poll every 30s for active calls
  });
};

export const useRecentDispositions = (limit = 20) => {
  return useQuery({
    queryKey: aiCallingQueryKeys.recentDispositions(limit),
    queryFn: () => getRecentDispositions(limit),
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 60 * 1000, // Poll every 60s for dispositions
  });
};

// ============================================================
// Usage Hooks
// ============================================================

export const useCurrentUsage = () => {
  return useQuery({
    queryKey: aiCallingQueryKeys.currentUsage(),
    queryFn: getCurrentUsage,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useUsageHistory = (startDate?: string, endDate?: string) => {
  return useQuery({
    queryKey: aiCallingQueryKeys.usageHistory(startDate, endDate),
    queryFn: () => getUsageHistory(startDate, endDate),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

// ============================================================
// A2P Status & Instant Call Hooks
// ============================================================

export const useA2PStatus = () => {
  return useQuery({
    queryKey: [...aiCallingQueryKeys.config(), "a2p-status"],
    queryFn: () => a2pService.checkStatus(),
    staleTime: 5 * 60 * 1000, // Cache for 5 min — status rarely changes
    gcTime: 10 * 60 * 1000,
  });
};

export const useInstantCall = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { leadId: string; campaignId?: string }) =>
      instantCall(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.activeCalls() });
      successToast("AI call initiated");
    },
    onError: () => errorToast("Failed to initiate AI call"),
  });
};

// ============================================================
// AI Texting Hooks
// ============================================================

export const useTextingEligibility = () => {
  return useQuery({
    queryKey: aiCallingQueryKeys.textingEligibility(),
    queryFn: getTextingEligibility,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useTextingStats = () => {
  return useQuery({
    queryKey: aiCallingQueryKeys.textingStats(),
    queryFn: getTextingStats,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useTextConversations = (status?: string, page = 1, pageSize = 20) => {
  return useQuery({
    queryKey: aiCallingQueryKeys.textingConversations({ status, page, pageSize }),
    queryFn: () => getTextConversations(status, page, pageSize),
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useTextConversation = (id: string | null) => {
  return useQuery({
    queryKey: aiCallingQueryKeys.textingConversation(id ?? ""),
    queryFn: () => getTextConversation(id as string),
    enabled: !!id,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Start an AI text thread with a lead. A 412 (not eligible / lead can't be
 * texted) is NOT toasted here — callers surface `error.message` inline.
 */
export const useStartAiText = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: StartAiTextParams) => startAiText(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.texting() });
    },
    onError: (err: any) => {
      if (isPreconditionFailed(err)) return;
      errorToast(err?.message || "Failed to start the AI text conversation");
    },
  });
};

export const useTakeOverTextConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => takeOverTextConversation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.texting() });
      successToast("You've taken over — reply from the SMS inbox");
    },
    onError: (err: any) => errorToast(err?.message || "Failed to take over the conversation"),
  });
};

export const useCloseTextConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => closeTextConversation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiCallingQueryKeys.texting() });
      successToast("Conversation closed");
    },
    onError: (err: any) => errorToast(err?.message || "Failed to close the conversation"),
  });
};

// ============================================================
// Realtime: campaign auto-pause / resume events
// ============================================================

interface CampaignEventPayload {
  campaignId?: string;
  campaignName?: string;
  reason?: string;
  pausedReason?: string;
}

interface AiTextEventPayload {
  conversationId?: string;
  leadId?: string;
  leadName?: string;
  reason?: string;
  action?: string;
  outcome?: string;
  status?: string;
}

/**
 * Listens on the user's `ai-calling:{userId}` channel (same realtime service the
 * dial-credits hook uses) for `campaign_paused_no_credits`, `campaign_auto_paused`
 * and `campaign_resumed_credits_added`, surfaces a toast and refreshes campaign
 * queries. Also handles the AI Texting events (`ai_text_started`, `ai_text_update`,
 * `ai_text_handoff`) — refreshes the texting queries and toasts on a handoff.
 * Best-effort — polling keeps the lists fresh if the socket is down.
 *
 * Mount ONCE per page (the Command Center page does) — a second subscriber on
 * the same topic would double-toast.
 */
export const useAiCampaignEvents = (opts?: { enabled?: boolean }) => {
  const qc = useQueryClient();
  const { user } = useUserContext();
  const userId = (user as any)?.user?.id || (user as any)?.id;
  const enabled = opts?.enabled ?? true;
  const { rejoinToken, handleStatus } = useRealtimeRejoin("ai-calling");

  useEffect(() => {
    if (!enabled || !userId) return;
    let channel: any = null;
    let cancelled = false;

    const refresh = () => {
      qc.invalidateQueries({ queryKey: aiCallingQueryKeys.campaigns() });
      qc.invalidateQueries({ queryKey: aiCallingQueryKeys.dashboard() });
    };

    getSupabaseClient()
      .then((supabase) => {
        if (cancelled || !supabase) return;
        channel = supabase.channel(`ai-calling:${userId}`);

        channel.on(
          "broadcast",
          { event: "campaign_paused_no_credits" },
          (msg: any) => {
            const data = (msg?.payload ?? {}) as CampaignEventPayload;
            toast.warning(
              data.campaignName ? `${data.campaignName} paused` : "Campaign paused",
              { description: data.reason || "Out of AI calling minutes. Top up to resume." },
            );
            refresh();
          },
        );
        channel.on(
          "broadcast",
          { event: "campaign_auto_paused" },
          (msg: any) => {
            const data = (msg?.payload ?? {}) as CampaignEventPayload;
            toast.warning(
              data.campaignName ? `${data.campaignName} paused` : "Campaign paused",
              { description: data.reason || "The campaign was paused automatically." },
            );
            refresh();
          },
        );
        channel.on(
          "broadcast",
          { event: "campaign_resumed_credits_added" },
          (msg: any) => {
            const data = (msg?.payload ?? {}) as CampaignEventPayload;
            toast.success(
              data.campaignName ? `${data.campaignName} resumed` : "Campaign resumed",
              { description: "Dial minutes added — calling has picked back up." },
            );
            refresh();
          },
        );


        // ── AI Texting ──
        const refreshTexting = () => {
          qc.invalidateQueries({ queryKey: aiCallingQueryKeys.texting() });
        };
        channel.on("broadcast", { event: "ai_text_started" }, () => refreshTexting());
        channel.on("broadcast", { event: "ai_text_update" }, () => refreshTexting());
        channel.on(
          "broadcast",
          { event: "ai_text_handoff" },
          (msg: any) => {
            const data = (msg?.payload ?? {}) as AiTextEventPayload;
            const who = data.leadName?.trim() || "A lead";
            toast.warning(`${who} needs you — the AI handed the text thread to you`, {
              description: data.reason || "Reply from the SMS inbox to pick up where the AI left off.",
            });
            refreshTexting();
          },
        );

        channel.subscribe(handleStatus);
      })
      .catch(() => {
        /* best-effort */
      });

    return () => {
      cancelled = true;
      if (channel) {
        try {
          removeRealtimeChannel(channel);
        } catch {
          /* noop */
        }
      }
    };
  }, [enabled, userId, qc, rejoinToken, handleStatus]);
};
